<template>
    <div class="shrink-0 relative z-[1000]" :class="[collapse? 'common-nav-wide': 'common-nav-collapse', isBlur && 'blur-sm pointer-events-none']">
        <PtAIHistoryNav
            v-model="collapse"
            :menus="menus"
            :theme="theme"
            :historyMenu="historyItem"
            :messages="messages"
            :responsive="!withoutResponsive"
            @clickHistory="viewResult"
            @removeHistory="onRemoveHistory"
        >
            <!-- pc端logo -->
            <template #productLogo="{ opened }" v-if="!withoutSections?.includes(NavSection.HEADER)">
                <a href="/air" class="common-nav-wide-logo w-full py-5 relative">
                    <span class="px-3 flex justify-center">
                        <Logo v-if="opened" class="w-[196px] h-[32px]"/>
                        <MiniLogo v-else class="w-6 h-6"/>
                    </span>
                </a>
            </template>
            <!-- 移动端logo -->
            <template #logo v-if="!withoutSections?.includes(NavSection.HEADER)">
                <a href="/air">
                    <img :src="mobileLogo" alt="mini-logo" class="!h-6">
                </a>
            </template>
            <template #avatar="{ opened }"  v-if="!withoutSections?.includes(NavSection.FOOTER)">
                <div class="mt-auto border-solid border-white/20 mx-3 flex flex-row items-center gap-4 xl:border-t xl:flex-col xl:pt-4 xl:pb-5 ">
                    <Incognito v-if="incognitoVisible" class="w-full" :opened="opened" :menu="incognitoItem" @handleClick="removeAllHistory"/>
                    <PtNavUserAvatar :open="opened" :service="headerService" @clickLogin="emit('clickLogin')" />
                </div>
            </template>
        </PtAIHistoryNav>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { PtAIHistoryNav, PtNavUserAvatar } from 'patsnap-biz';
import type { HeaderService } from '@patsnap-utils/biz';
import MiniLogo from './components/icons/MiniLogo.vue';
import Logo from './components/icons/Logo.vue';
import mobileLogo from './assets/img/Logo.png';
import Incognito from './components/Incognito.vue';
import { cn, tw, en, jp } from './infrastructure/locales';
import { createMenus, getNewsletterSourceToMenuPath, HISTORY_MENU_ITEM, INCOGNITO_MENU_ITEM, ROUTE_NAME_WHITELIST } from './constants';
import { useNavBarState } from './composables/use-navbar-store';
import type { HistoryItem, NavMenuItem, NavTheme } from './types';
import { MenuPath, NavSection } from './types';
import { useNavBar } from './composables/use-nav-bar';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import { controlDeferred } from '@eureka/utils/control';

const { t, te } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

const props = defineProps<{
    headerService: HeaderService,
    isBlur?: boolean,
    theme?: NavTheme,
    withoutSections?: NavSection[],
    customMenus?: NavMenuItem[],
    withoutResponsive?: boolean,
    historyFilters?: string[],
}>();
const emit = defineEmits(['clickLogin']);
const { collapse, currentDetailId, routeName, shouldSave, currentRoute, routeQuery, updateNavStatus, queryList, historyList, save } = useNavBarState();

const { viewResult, removeHistoryItem, removeAllHistory } = useNavBar();

const fmeaFeature = ref(false);

const messages = ref({
    cn: {
        HistoryType: cn.HistoryType,
    },
    en: {
        HistoryType: en.HistoryType,
    },
    jp: {
        HistoryType: jp.HistoryType,
    },
    tw: {
        HistoryType: tw.HistoryType,
    },
})

watch(() => currentRoute.value, () => {
    if (_.includes(ROUTE_NAME_WHITELIST, routeName.value) || !currentDetailId.value) return;
    updateNavStatus(false, false);
}, { immediate: true });

watch(() => collapse.value, (value) => {
    if (shouldSave.value) {
        save(value as boolean);
    }
})

const activeMenu = computed(() => {
    const name = _.get(window.location, 'pathname', '')?.replace(/\//g, '')?.toLowerCase();
    if (currentDetailId.value) return '';
    if (name === '404' || !name) return MenuPath.AIR;

    const url = new URL(window.location);
    const hash = _.join(_.slice(_.get(url, 'hash', ''), 1), '');
    const hashSegments = _.split(hash, '?')?.[0];
    const subPath = _.split(hashSegments, '/')?.[1];
    const newsletterSource = getNewsletterSourceToMenuPath(_.get(routeQuery.value, 'source') as string);

    const active = _.find(_.values(MenuPath), (menu) => {
        return name === MenuPath.NEWSLETTER
            ? (menu === subPath || (subPath === MenuPath.DETAIL && menu === newsletterSource))
            : menu === name;
    });
    return active;
});

function getActualTitle(title?:string, id?: string) {
    if (title && te(title)) return t(title);
    if (id && te(`NavBar.${id}`)) return t(`NavBar.${id}`);
    return title;
}

const menus = computed(() => {
    if (props.customMenus) {
        return _.map(props.customMenus, item => {
            return {
                ...item,
                title: getActualTitle(item.title, item.id),
            }
        })
    }
    const menus = createMenus(fmeaFeature.value)
    return _.map(menus, item => {
        return {
            ...item,
            title: getActualTitle('', item.id),
            active: item.id === activeMenu.value,
            onClick: viewResult,
            children: _.map(item.children, child => {
                return {
                    ...child,
                    title: getActualTitle('', child.id),
                    active: child.id === activeMenu.value,
                    onClick: viewResult,
                }
            }),
        };
    });
});

const historyItem = computed(() => {
    return {
        ...HISTORY_MENU_ITEM,
        title: t(`NavBar.${HISTORY_MENU_ITEM.id}`),
        children: _.map(historyList.value, item => ({ ...item, active: item.id === currentDetailId.value })),
    };
});

const incognitoItem = computed(() => {
    return {
        ...INCOGNITO_MENU_ITEM,
        title: t(`NavBar.${INCOGNITO_MENU_ITEM.id}`),
        tip: t(`NavBar.${INCOGNITO_MENU_ITEM.id}Tip`),
    };
});

const incognitoVisible = computed(() => {
    return _.get(props.headerService.account, '_info.incognito_flag');
});
function onRemoveHistory(data: HistoryItem) {
    removeHistoryItem(data, props.historyFilters);
}

onMounted(async() => {
    queryList(props.historyFilters);
    const { tech_fmea } = await controlDeferred.promise;
    fmeaFeature.value = tech_fmea;
})
</script>
<style lang="scss">
// TODO 组件库里的颜色配错了，这里暂时覆盖，后续组件库修复后删除
.nav-theme-dark {
    --nav-bg-color: #0456D1;
}
.common-nav-wide-logo {
    &::after {
        content: '';
        @apply absolute left-3 right-3 bottom-0 h-px bg-white/20;
    }
}
.nav-history-sub-item {
    &.active-menu-item, &.tippy-content-active {
        span:last-child {
           display: none !important;
        }
    }
}
</style>
